import { config } from "../../config";

/**
 * The score/status pieces shared by the directory artwork card and the
 * directory-edit table.
 *
 * They lived twice: as JSX inside `DirectoryArtworkCard.tsx` and as
 * `buildScoreBoxes()` / an inline `statusText` chain inside `directoryUpload.ts`,
 * left over from a half-finished conversion. `buildScoreBoxes` was byte-identical
 * to `ScoreBoxes`, but the two status implementations had **drifted** — see the
 * note on `StatusDisplay`.
 */

/** The ten-box optimisation-score meter. */
export function ScoreBoxes({ score }: { score: number }) {
  const boxes = [];
  for (let i = 1; i <= 10; i++) {
    boxes.push(
      <span
        key={i}
        class={`view-data-scorebox2${score >= i ? " view-data-scorebox2-filled" : ""}`}
      />
    );
  }
  return <div class="directory-table-scoreboxes">{boxes}</div>;
}

/**
 * The coloured for-sale status dot and its label — two states, and they answer exactly one
 * question: is this artwork on sale right now?
 *
 * It used to have five ("Data missing", "Low resolution", "Expanded Licensing"). Artists
 * read every red label as "my artwork is not selling", and "Data missing" was noise, so it
 * became two. The first cut of that folded resolution into the sale question — anything
 * under 8 MP read "Not for sale" — which put a red "Not for sale" directly under the
 * artist's own "For sale" dropdown on the same card, for 18,372 artworks across 3,903
 * artists (reported 2026-09-23, artwork #40012906). Those artworks ARE on sale: they are in
 * browse, the reseller API and the download paths, since nothing in the code enforces the
 * 8 MP rule the upload requirements state. So the dot follows `stage` (and the artist's
 * hidden-sale setting) and nothing else.
 *
 * The resolution is still shown, because it is the artist's lever: a low-resolution artwork
 * sells badly and the rules say it should not sell at all, so the megapixel figure is
 * called out rather than hidden. See `todo/` — enforcing the 8 MP rule is a product
 * decision, not something this label can do on its own.
 */
export function StatusDisplay({ stage, megapixels }: { stage: string; megapixels: number | null }) {
  const forSale = !config.hiddenSale && stage === "READY_FOR_SALE";
  if (!forSale) {
    return (
      <>
        <span class="directory-dot-red" /> Not for sale
      </>
    );
  }
  return (
    <>
      <span class="directory-dot-green" /> For sale
      {megapixels !== null && (
        <>
          , {megapixels} MP
          {megapixels <= 8 && <span class="directory-lowres"> &mdash; low resolution</span>}
        </>
      )}
    </>
  );
}
